import { useEffect, useState, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { RepositoryContext } from '../../store/RepositoryProvider';
import Button, { BUTTON_POSITION, BUTTON_TYPE } from '../../components/Button';
import Price, { CURRENCY } from '../../components/Price';
import API from '../../server/api';
import Alert, { ALERT_TYPE } from '../../components/Alert';
import { attachAndroidEvent, payToPhone, removeAndroidEvent } from '../../utils/events';
import { PaymentMethod, PaymentTransaction, PayToPhoneMethod } from '../../models/types';
import Field from '../../components/Field';
import log from '../../utils/logger';
import { env } from '../../utils/util';

const Payment = ({order, comment = '', amount, type, onSuccess, onCancel, onFail, onAwaiting}) => {
  const {t} = useTranslation(['common', 'order']);

  const repository = useContext(RepositoryContext);

  const [oddMoney, setOddMoney] = useState(0);
  const [money, setMoney] = useState(0);
  const [paymentStatus, setPaymentStatus] = useState({});
  const [errors, setErrors] = useState([]);

  const handlePayment = useCallback(async () => {
    setErrors([]);
    if (type === PaymentMethod.CASH) {
      if (money < amount) {
        setErrors([{ code: 'payment_error', type: ALERT_TYPE.ERROR, title: t('order:error.not_enough_money'), message: t('order:error.not_enough_money') }]);
      } else {
        repository.action(API.order.cashPayment, {
          amount: amount,
          id: order,
          comment: comment
        }).then((payment) => {
          onSuccess && onSuccess(amount);
        }).catch((error) => {
          log.error(error);
          onFail && onFail(t(`order:payment.error.${error.error.toLowerCase()}`));
        });
      }
    } else if (type === PaymentMethod.CARD) {
      payToPhone(amount, PayToPhoneMethod.NFC).catch((e) => {
        setErrors([{ code: 'apk_error', type: ALERT_TYPE.ERROR, title: t(`order:error.${e && e.error ? e.error : 'payment_error'}`), message: t('order:error.invalid_payment_type') }]);
      });
    } else if (type === PaymentMethod.QRCODE) {
      payToPhone(amount, PayToPhoneMethod.QR).catch((e) => {
        setErrors([{ code: 'apk_error', type: ALERT_TYPE.ERROR, title: t(`order:error.${e && e.error ? e.error : 'payment_error'}`), message: t('order:error.invalid_payment_type') }]);
      });
    } else if (type === PaymentMethod.ONLINE) {
      onAwaiting && onAwaiting(amount);
    }
  }, [money]);

  const handlePaymentClose = useCallback(async () => {
    onCancel && onCancel();
  }, []);

  const handleClientCash = useCallback((value) => {
    let clientCash = Number(value);
    if (clientCash) {
      setOddMoney(Math.max(clientCash - amount, 0));
    }

    setMoney(clientCash);
  }, []);

  useEffect(() => {
    attachAndroidEvent('payment', async (e) => {
      const paymentData = new PaymentTransaction(e.detail);

      if (paymentData.error) {
        log.error('Payment status:', paymentData.error);

        let title = t('order:payment.error_card_status');
        let message = paymentData.error;
        if (message.toLowerCase().indexOf('Failed to bind to Pay to phone'.toLowerCase()) >= 0) {
          title = t('common:error.pay_to_phone_app_no_launch');
          message = t('common:pay_to_phone_app_no_launch.todo') + '\n';
          message += t('common:pay_to_phone_app_no_launch.step1') + '\n';
          if (paymentData.manufacturer && paymentData.manufacturer.toLowerCase() === 'tecno') {
            message += t('common:pay_to_phone_app_no_launch.step2_tecno');
          } else {
            message += t('common:pay_to_phone_app_no_launch.step2_other');
          }
        }

        setPaymentStatus({ error: message });
        setErrors([{ code: 'payment_error', type: ALERT_TYPE.ERROR, title, message }]);
        // onFail && onFail(message);
      } else {
        if (type === PaymentMethod.CARD || type === PaymentMethod.QRCODE) {
          setPaymentStatus({ type, rrn: paymentData.transaction_id });
          repository.action(API.order.nfcPayment, {
            amount: paymentData.amount / 100,
            orderId: order,
            comment: comment,
            rrn: paymentData.transaction_id,
            mid: paymentData.mid,
            method: type === PaymentMethod.CARD ? 'nfc' : 'qr',
            terminalKey: paymentData.tid,
            transactionDate: new Date().toISOString()
          }).then(() => {
            onSuccess && onSuccess(paymentData.amount / 100);
            setPaymentStatus({ type, status: t('order:payment.paid') });
          }).catch((error) => {
            log.error(error);
            setPaymentStatus({ type, status: t('order:payment.no_paid') });
            onFail && onFail(t(`order:payment.error.${error.error.toLowerCase()}`));
          });
        }
      }
    });

    return () => {
      removeAndroidEvent('payment');
    };
  }, []);

  const handleCloseError = (uid) => {
    setErrors(errors.filter((e) => e.uid !== uid));
  };


  return (
    <div>
      {(errors.length > 0) && errors.map((error, i) => (
        <Alert type={error.type} key={error.uid || i} title={error.title} dismissible={true} onClose={() => handleCloseError(error.uid)}>
          {error.message && error.message.split('\n').map((m) => (
            <div>{m}</div>
          ))}
        </Alert>
      ))}
      {(type === PaymentMethod.CARD || type === PaymentMethod.QRCODE || type === PaymentMethod.ONLINE) && (
        <Field title={t('order:payment.total_amount')}>
          <Price currency={CURRENCY.RUB} value={amount} />
        </Field>
      )}
      {(type === PaymentMethod.CASH) && (
        <>
          <Field title={t('order:payment.total_amount')}>
            <Price currency={CURRENCY.RUB} value={amount} />
          </Field>
          <Field title={t('order:payment.client_cash')}>
            <Price.Input onInput={handleClientCash} />
          </Field>
          <Field title={t('order:payment.odd_money')}>
            <Price currency={CURRENCY.RUB} value={oddMoney} />
          </Field>
        </>
      )}

      {paymentStatus && (
        <Field>
          {paymentStatus.status}
        </Field>
      )}

      <Button.Panel>
        {(type === PaymentMethod.CASH) && (
          <Button position={BUTTON_POSITION.LEFT} variant={BUTTON_TYPE.PRIMARY} onClick={handlePayment}>{t('common:button.paid')}</Button>
        )}
        {(type === PaymentMethod.CARD) && (
          <Button position={BUTTON_POSITION.LEFT} variant={BUTTON_TYPE.PRIMARY} onClick={handlePayment}>{t('order:payment_booking.nfc')}</Button>
        )}
        {(type === PaymentMethod.QRCODE) && (
          <Button position={BUTTON_POSITION.LEFT} variant={BUTTON_TYPE.PRIMARY} onClick={handlePayment}>{t('order:payment_booking.scan')}</Button>
        )}
        {(type === PaymentMethod.ONLINE) && (
          <Button position={BUTTON_POSITION.LEFT} variant={BUTTON_TYPE.PRIMARY} onClick={handlePayment}>{t('order:payment_booking.complete')}</Button>
        )}
        <Button position={BUTTON_POSITION.RIGHT} variant={BUTTON_TYPE.SECONDARY} onClick={handlePaymentClose}>{t('common:button.cancel')}</Button>
      </Button.Panel>
    </div>
  );
};

Payment.propTypes = {
  type: PropTypes.string,
  onSuccess: PropTypes.func,
  onCance: PropTypes.func,
  onFail: PropTypes.func,
};

export default Payment;